import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CachedProjectService } from "../../../../shared/services/cached-project.service";
import { ImageLoaderService } from "../../../../shared/services/image-loader.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import * as d3 from "d3";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { ProjectService } from "../../../../shared/services/project.service";
import { cloneDeep } from "lodash";

interface RangeGroup {
  label: string;
  indices: number[];
  selected: boolean;
}

@Component({
  selector: "app-spatial-plotting-v2",
  templateUrl: "./spatial-plotting-v2.component.html",
  styleUrls: ["./spatial-plotting-v2.component.less"],
  standalone: false,
})
export class SpatialPlottingV2Component {
  @ViewChild("graph", { static: true }) graph: ElementRef;
  @ViewChild("graphContainer", { static: true }) container: ElementRef;
  @ViewChild("infoContainer", { static: true }) infoContainer: ElementRef;

  private destroy$ = new Subject<void>();
  @Input() shotData: any;
  @Input() selectedShotId: any;
  @Input() iteration: number;
  @Input() rcvrData: any;
  @Input() height: number = 500;
  @Input() panel: "Prediction" | "Field" | "Interleaved" | "Phase Difference" =
    "Prediction";
  @Input() totalTime: number = 0;

  @Input() rcvrsBoundingBox!: any[];
  @Output() rcvrsBoundingBoxChange = new EventEmitter<any[]>();

  @Input() startTime: number = null;
  @Input() endTime: number = null;

  @Input() muteSlope: number[] = [];
  @Input() muteIntersection: number[] = [];
  @Input() muteOffset: number[] = [];
  @Input() muteDistanceTypes: string[] = []
  @Input() muteOn: boolean[] = []
  @Input() mute: boolean
  

  @Input() filterSettings: object;
  @Output() filterSettingsChange = new EventEmitter<any>();

  @Output() updateColorbarLimits = new EventEmitter<any>();

  shot: any;
  dx: number;
  jobId: string;
  shotDisplayType: "lowpass-B4" | "compare" = "lowpass-B4";
  panelCount: number = 2;
  shotImageString: string;
  rcvrsInBoundingBox: any;
  interleave_extent: number = 10;
  scaling: number = 1;

  sortingIndex: "id" | "distance" | "inline" | "crossline" = "id";
  sortingDirection: "asc" | "desc" = "asc";
  rcvrInterval: number = 10;
  scale_fact_power: number = 0.75;
  // scaling as last sent to the server, so committing the field only replots
  // when the number actually changed — a blur that edited nothing shouldn't
  // cost a request
  private plottedScaling: number = null;

  // d3 variables
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  xScale: any;
  yScale: any;
  xAxis: any;
  yAxis: any;

  // bounding box variables
  xOffset: number = 0;
  yOffset: number = 0;
  boxHeight: number = 100;
  boxWidth: number = 5;

  padding: { left: number; right: number; top: number; bottom: number } = {
    left: 30,
    right: 30,
    top: 10,
    bottom: 40,
  };
  isLoading: boolean;
  width: any;

  // Selected receivers modal
  showReceiversModalVisible: boolean = false;
  rangeGroups: RangeGroup[] = [];
  rcvrDistance: {
    'xy': number[],
    'xyz': number[],
    'xz': number[],
    'yz': number[],
  };

  constructor(
    private cachedProjectService: CachedProjectService,
    private projectService: ProjectService,
    private imageService: ImageLoaderService,
    private notificationService: NzNotificationService,
  ) {}

  ngOnInit() {
    this.cachedProjectService.currentJob
      .pipe(takeUntil(this.destroy$))
      .subscribe((job) => {
        this.jobId = job.id;
        this.dx = job.modelGrid.dx;
        this.updateSettingsFromURL(this.filterSettings);
        this.updateShotGraph();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cropImage(start_time, end_time) {
    return new Promise<string>((resolve, reject) => {
      let img = document.createElement("img");
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        const cropWidth = img.naturalWidth;
        let startHeight = (img.naturalHeight / this.totalTime) * start_time;
        let endHeight = (img.naturalHeight / this.totalTime) * end_time;
        const cropHeight = endHeight - startHeight;
        canvas.width = this.width;
        canvas.height = this.height;

        context.imageSmoothingQuality = "high";
        context.drawImage(
          img,
          0,
          startHeight,
          cropWidth,
          cropHeight,
          0,
          0,
          this.width,
          this.height,
        );
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = (error) => {
        reject(error);
      };
      img.src = this.shotImageString;
    });
  }

  rescaleOnTimeDimension() {
    if (this.svg && this.yScale && this.yAxis) {
      let valueMax = this.endTime || this.totalTime;
      let valueMin = this.startTime || 0;
      this.yScale.domain([valueMin, valueMax]);

      this.svg.select(".y-axis").transition().duration(500).call(this.yAxis);
      this.cropImage(valueMin, valueMax).then((croppedImage) => {
        this.svg.select("image").attr("xlink:href", croppedImage);
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if ("startTime" in changes || "endTime" in changes) {
      this.rescaleOnTimeDimension();
    }
    if ("panel" in changes) {
      this.updateShotGraph();
    }
    if ("selectedShotId" in changes) {
      this.shot = this.shotData?.find((s) => s.shotId == this.selectedShotId);
      this.resetBoundingBoxToShot();
    }
    if ("rcvrsBoundingBox" in changes) {
      this.update_box_parameters();
    }

    if (
      "muteSlope" in changes ||
      "muteIntersection" in changes ||
      "muteOffset" in changes || 
      "muteOn" in changes ||
      "muteDistanceTypes" in changes 
    ) {
      this.makeMutes()
    }
  }

  private makeMutes(){ 
    if (!this.svg) return 
    // a get or create on the mutes G
    let mutesG = this.svg?.select("g.mutes-group");
    if (!mutesG || mutesG?.empty()) mutesG = this.svg?.append("g").attr("class", "mutes-group");
    
    // remove all from before if exists
    mutesG.raise()
    mutesG.selectAll("path").remove();

    // if the mutes are overall turned off, just don't plot the mutes after removing them from the graph
    if (!this.mute) return

    const lineGenerator = d3.line<number>().x((_, index) => this.xScale(index)).y((y) => this.yScale(y))
    let colors = ["grey", "#00FF00", "yellow"];
    for (let i = 0; i < 3; i++) {
      // if this specific mute is not supposed to be shown, skip
      if (!this.muteOn[i]) continue
      const slope = this.muteSlope[i];
      const intercept = this.muteIntersection[i];
      const offset = this.muteOffset[i]
      const color = colors[i]
      // 0 --> xyEuclidean,
      // 1 --> xyzEuclidean,
      // 2 --> xzEuclidean,
      // 3 --> yzEuclidean,
      const distanceType = ['xy', 'xyz', 'xz', 'yz'][this.muteDistanceTypes[i]]
      
      let actual_intersection = intercept - offset * (1 / slope)
      let lineEquation = (rcvrDistance: number) => actual_intersection + rcvrDistance * (1 / slope)
      let lineData = this.rcvrDistance[distanceType].map((d: number) => lineEquation(d))
      mutesG
        .append("path")
        .attr("fill", "none")
        .attr("stroke", color)
        .attr("stroke-width", 3)
        .attr("stroke-opacity", 0.7)
        .attr("d", lineGenerator(lineData))
        .attr("class", "line");
    }
  }

  resetBoundingBoxToShot() {
    this.xOffset = 0;
    this.yOffset = 0;
    this.boxWidth = 50;
    this.boxHeight = 50;
    this.onBoundsChanged();
  }

  updateSettingsFromURL(settings: object) {
    this.rcvrsBoundingBox = settings["bounding_box"];
    this.scaling = Number(settings["image_scaling"]);
    this.rcvrInterval = settings["rcvr_interval"];
    this.sortingIndex = settings["sort_ind"];
    this.sortingDirection = settings["sort_dir"];
    this.rcvrsBoundingBoxChange.emit([
      [this.rcvrsBoundingBox[0][0], this.rcvrsBoundingBox[0][1]],
      [this.rcvrsBoundingBox[1][0], this.rcvrsBoundingBox[1][1]],
    ]);
    this.update_box_parameters();
  }

  updateShotGraph(forceReplot: boolean = false) {
    // check if bounding box exists
    if (
      !this.rcvrsBoundingBox ||
      !this.rcvrsBoundingBox[0] ||
      !this.rcvrsBoundingBox[1] ||
      (this.rcvrsBoundingBox[0][0] === 0 &&
        this.rcvrsBoundingBox[0][1] === 0 &&
        this.rcvrsBoundingBox[1][0] === 0 &&
        this.rcvrsBoundingBox[1][1] === 0)
    ) {
      return;
    }

    // resolve rcvrs that are in the bounding box
    let startX = this.rcvrsBoundingBox[0][0]; // in real units
    let startY = this.rcvrsBoundingBox[0][1]; // in real units
    let endX = this.rcvrsBoundingBox[1][0]; // in real units
    let endY = this.rcvrsBoundingBox[1][1]; // in real units

    this.rcvrsInBoundingBox = this.rcvrData.filter(
      (rcvr: { x: number; y: number }) =>
        rcvr.x > startX && rcvr.x < endX && rcvr.y < startY && rcvr.y > endY,
    ); // the limits of y are inverted. This is because the y axis is inverted in representation and plot

    if (
      this.rcvrsInBoundingBox.filter((rcvr, index) => {
        return index % this.rcvrInterval == 0;
      }).length > 1200
    ) {
      this.notificationService.warning(
        "Too many recievers selected",
        `You have selected ${this.rcvrsInBoundingBox.length} recievers. You can only select a maximum of 1200 recievers.`,
      );
    } else if (this.rcvrsInBoundingBox.length == 0) {
      this.notificationService.warning(
        "No recievers selected",
        `You have not selected any recievers.`,
      );
    } else {
      this.getShotGraph(forceReplot);
    }
  }

  euclidean(x1: number, x2: number, y1: number, y2: number) {
    return Math.sqrt(Math.pow(x1 - x2, 2) + Math.pow(y1 - y2, 2));
  }

  getShotGraph(forceReplot: boolean = false) {
    if (!this.jobId) return;

    let rcvrIndices = this.rcvrsInBoundingBox.map((rcvr: {shotId: number}) => rcvr.shotId)
    let panelIndex =
      this.panel == "Prediction"
        ? 0
        : this.panel == "Field"
          ? 1
          : this.panel == "Interleaved"
            ? 3
            : 4;

    let parameters = {
      dataType: this.shotDisplayType,
      panel: panelIndex,
      indices: rcvrIndices,
      shotId: this.shot.shotId,
      interleave_extent: this.interleave_extent,
      iteration: this.iteration,
      clipping: Number(this.scaling),
      num_panel: this.panelCount,
      force_plot: forceReplot,
      sortingIndex: this.sortingIndex,
      sortingDirection: this.sortingDirection
    };

    if (this.panel == "Phase Difference") {
      parameters["scale_fact_power"] = Number(this.scale_fact_power);
      parameters["max"] = Number(this.scaling);
    }

    this.plottedScaling = Number(this.scaling);

    this.isLoading = true;
    // make request to spatialPlotting endpoint and get image
    this.projectService
      .get_spatial_plotting(this.jobId, parameters)
      .toPromise()
      .then((res) => {
        this.shotImageString = "data:image/png;base64," + res["image_string"];
        this.rcvrDistance = res['rcvr_distances']

        if (this.panel != "Phase Difference") {
          this.updateColorbarLimits.emit(res["data"]["clip_value"]);
        }

        this.plotShotGraph();
        this.rescaleOnTimeDimension();
        this.filterSettingsChange.emit({
          ...this.filterSettings,
          bounding_box: this.rcvrsBoundingBox,
          image_scaling: this.scaling,
          rcvr_interval: this.rcvrInterval,
          sort_ind: this.sortingIndex,
          sort_dir: this.sortingDirection,
          panel: this.panel,
        });
        this.rcvrsBoundingBoxChange.emit(cloneDeep(this.rcvrsBoundingBox));

        let save_params = {};
        if (this.panel == "Interleaved")
          save_params["interleave_extent"] = this.interleave_extent;
        // this.updateURLParams.emit(save_params);
      })
      .catch((err) => {
        console.error("Error in getting spatial plotting: ", err);
        this.notificationService.error(
          "Error",
          err.error?.error || "There was an unexpected error.",
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  // Scaling reaches the plot as the server-side clipping value, so a new number
  // needs a refetch: replot as soon as the field is committed (enter or blur)
  // instead of leaving the image stale until "Plot Graph" is pressed.
  onScalingCommitted() {
    if (this.scaling == null || Number(this.scaling) === this.plottedScaling) {
      return;
    }
    this.updateShotGraph();
  }

  shotDisplayTypeChanged(shotDisplayType: string) {
    if (shotDisplayType == "compare") this.panelCount = 3;
    else if (shotDisplayType == "lowpass-B4") this.panelCount = 2;
  }

  plotShotGraph() {
    // for the case when SVG already exists so remove it for updating. Could also have just editted it really but makes the code complicated
    this.svg?.select("image")?.remove();
    this.svg?.select("g.x-axis")?.remove();
    this.svg?.select("g.y-axis")?.remove();

    const containerWidth = this.container.nativeElement.offsetWidth;
    this.width = containerWidth;

    this.svg = d3
      .select(this.graph.nativeElement)
      .attr("width", containerWidth)
      .attr("height", this.height + this.padding.top + this.padding.bottom);

    // Sett the image in the svg element
    this.svg
      .append("image")
      .attr("xlink:href", this.shotImageString)
      .attr("x", this.padding.left)
      .attr("preserveAspectRatio", "none")
      .attr("y", this.padding.top)
      .attr("width", containerWidth - this.padding.left - this.padding.right)
      .attr("height", this.height);

    // define yScale and axis
    this.yScale = d3
      .scaleLinear()
      .domain([0, this.totalTime])
      .range([this.padding.top, this.height + this.padding.top]);
    this.yAxis = d3.axisLeft(this.yScale);

    // define xScale and axis
    this.xScale = d3
      .scaleLinear()
      .domain([0, this.rcvrsInBoundingBox.length])
      .range([this.padding.left, containerWidth - this.padding.right]);
    this.xAxis = d3.axisBottom(this.xScale);

    // append the axes to the SVG
    this.svg
      .append("g")
      .attr("class", "y-axis")
      .attr("transform", `translate(${this.padding.left},0)`)
      .call(this.yAxis);
    this.svg
      .append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0,${this.height + this.padding.top})`)
      .call(this.xAxis);
    this.svg
      .append("text")
      .attr("class", "x-axis-label")
      .attr("text-anchor", "middle")
      .attr(
        "x",
        this.padding.left +
          (containerWidth - this.padding.left - this.padding.right) / 2,
      )
      .attr("y", this.height + this.padding.top + this.padding.bottom - 5)
      .text("Number of Traces");

    this.svg
      .append("text")
      .attr("class", "y-axis-label")
      .attr("text-anchor", "middle")
      .attr("transform", `rotate(-90)`)
      .attr("x", -(this.padding.top + this.height / 2))
      .attr("y", 15)
      .text("Time(s)");
      
    this.makeMutes()
  }

  updateHeight() {
    d3.select(this.graph.nativeElement).attr(
      "height",
      this.height + this.padding.top + this.padding.bottom,
    );
    if (this.svg) {
      this.svg.select("image").attr("height", this.height);

      this.yScale.range([0, this.height]);
      this.yAxis = d3.axisLeft(this.yScale);
      this.svg.select(".y-axis").call(this.yAxis);

      this.xAxis = d3.axisBottom(this.xScale);
      this.svg
        .select(".x-axis")
        .call(this.xAxis)
        .attr("transform", `translate(0,${this.height})`);
    }
  }

  onBoundsChanged() {
    // The calculation for cx il is a bit finicky. I'll try to explain aptly.
    // cx --> x-axis, il --> y-axis  ((all real valyes))
    // shot.x and shot.y are in real values
    // xOffset and yOffset are from the center of the shot
    // the width addition and substraction is flipped in cx and il. this is because the y-axis is flipped with greater values on top and smaller on the bottom
    // point is that the cx_start end and il_start and end how you name doesn't matter, simply the array representation emitted should have the top left co-ords in the first array and the bottom right in the second.

    let cx_start =
      this.shot.x + this.xOffset * this.dx - (this.boxWidth * this.dx) / 2;
    let cx_end =
      this.shot.x + this.xOffset * this.dx + (this.boxWidth * this.dx) / 2;

    let il_start =
      this.shot.y + this.yOffset * this.dx + (this.boxHeight * this.dx) / 2;
    let il_end =
      this.shot.y + this.yOffset * this.dx - (this.boxHeight * this.dx) / 2;

    if (isNaN(cx_start) || isNaN(il_start) || isNaN(il_end) || isNaN(cx_end)) {
      return;
    }
    this.rcvrsBoundingBoxChange.emit([
      [cx_start, il_start],
      [cx_end, il_end],
    ]);
  }

  update_box_parameters() {
    // this is basically the inverse of onBoundsChanged
    // look at the description of that to understand further the relationships between all the below mentioned variables

    if (this.shot) {
      var [[cx_start, il_start], [cx_end, il_end]] = this.rcvrsBoundingBox;

      if (cx_start === 0 && il_start === 0 && cx_end === 0 && il_end === 0) {
        return;
      }

      let cx_start_dx = cx_start / this.dx;
      let il_start_dx = il_start / this.dx;
      let cx_end_dx = cx_end / this.dx;
      let il_end_dx = il_end / this.dx;

      this.boxHeight = parseFloat((il_start_dx - il_end_dx).toFixed(0));
      this.boxWidth = parseFloat((cx_end_dx - cx_start_dx).toFixed(0));

      this.xOffset = parseFloat(
        (cx_start_dx + this.boxWidth / 2 - this.shot.x / this.dx).toFixed(0),
      );
      this.yOffset = parseFloat(
        (il_start_dx - this.boxHeight / 2 - this.shot.y / this.dx).toFixed(0),
      );
    }
  }

  private groupConsecutiveNumbers(numbers: number[]): RangeGroup[] {
    if (!numbers || numbers.length === 0) {
      return [];
    }

    const sorted = [...numbers].sort((a, b) => a - b);
    const groups: RangeGroup[] = [];
    let currentGroup: number[] = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === sorted[i - 1] + 1) {
        currentGroup.push(sorted[i]);
      } else {
        groups.push({
          label:
            currentGroup.length === 1
              ? currentGroup[0].toString()
              : `${currentGroup[0]}-${currentGroup[currentGroup.length - 1]}`,
          indices: [...currentGroup],
          selected: false,
        });
        currentGroup = [sorted[i]];
      }
    }

    // add last group
    groups.push({
      label:
        currentGroup.length === 1
          ? currentGroup[0].toString()
          : `${currentGroup[0]}-${currentGroup[currentGroup.length - 1]}`,
      indices: [...currentGroup],
      selected: false,
    });

    return groups;
  }

  showReceiversModal(): void {
    this.rangeGroups = this.groupConsecutiveNumbers(
      this.rcvrsInBoundingBox.map((rcvr: { shotId: number }) => rcvr.shotId),
    );
    this.showReceiversModalVisible = true;
  }

  closeReceiversModal(): void {
    this.showReceiversModalVisible = false;
    this.rangeGroups = [];
  }

  copyAllIndices(): void {
    const allIndices = this.rangeGroups.map((group) => group.label).join(",");
    navigator.clipboard
      .writeText(allIndices)
      .then(() => {
        this.notificationService.success(
          "Copied",
          "All receiver indices copied to clipboard",
        );
      })
      .catch(() => {
        this.notificationService.error("Error", "Failed to copy to clipboard");
      });
  }

  copySelectedRanges(): void {
    const selectedIndices = this.rangeGroups
      .filter((group) => group.selected)
      .map((group) => group.label)
      .join(",");

    if (selectedIndices) {
      navigator.clipboard
        .writeText(selectedIndices)
        .then(() => {
          this.notificationService.success(
            "Copied",
            "Selected receiver indices copied to clipboard",
          );
        })
        .catch(() => {
          this.notificationService.error(
            "Error",
            "Failed to copy to clipboard",
          );
        });
    }
  }

  // check if any ranges are selected
  get hasSelectedRanges(): boolean {
    return this.rangeGroups.some((group) => group.selected);
  }
}
